/** Temas de ayuda · Caja y control: caja, cierres, autorizaciones, consolidado, configuración, permisos y auditoría. */
import { VE_TASA_2, type TemaAyuda } from '../tipos.ts';

export const TEMAS_CAJA_CONTROL: TemaAyuda[] = [
  // ─── Caja ────────────────────────────────────────────────────────────────
  {
    id: 'caja',
    titulo: 'Caja',
    rutas: ['/caja'],
    permiso: { clave: 'caja.abrir' },
    resumen: 'Tu sesión de caja: la abres con el fondo de la caja, sacas efectivo con un retiro parcial y la cierras contando a ciegas. Aquí no se ve lo recaudado.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          '**Mi sesión**: sin caja abierta, el formulario para abrirla. Con caja abierta, su nombre, el **turno** del día, la hora de apertura, el **Fondo $** y el **Fondo Bs**, y los botones **Retiro parcial** y **Contar y cerrar caja**.',
          '**Sesiones de la sucursal**: las últimas 20 aperturas, con caja, cajero, apertura, fondo y estado (**Abierta**, **Conteo declarado** o **Cerrada**). La dueña y quien autoriza cierres ven el botón **Retiro** en las cajas abiertas de otros cajeros.',
          '**Retiros parciales de efectivo**: el efectivo que salió de la caja durante el turno, con su estado y el botón para imprimir su comprobante. El cajero ve los de su caja, también los que registró otra persona sobre ella; el administrador y la dueña, los de la sucursal.',
          'Esa lista se acota con **Desde** y **Hasta** (el botón **Hoy** pone el día de hoy y **×** quita las fechas). Salen 25 retiros por página: si hay más, abajo dice cuántos son y con **Anterior** y **Siguiente** ves los demás.',
          'Una caja marcada **Solo facturas** emite facturas y no notas de entrega. Tampoco cobra el saldo de una nota.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Abrir caja',
        pasos: [
          'En **Mi sesión**, elige la **Caja**.',
          'El sistema propone el fondo fijo de esa caja en **Fondo inicial $** y **Fondo inicial Bs**. Cuenta lo que hay en la gaveta.',
          'Si coincide, déjalo así. Si no, escribe lo que hay: la caja abre con eso y la dueña recibe el aviso.',
          'Pulsa **Abrir caja**. La cabecera muestra tu caja abierta y la tarjeta dice el turno.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Cerrar la caja',
        pasos: [
          'Pulsa **Contar y cerrar caja**.',
          'Cuenta y escribe lo que hay en cada método, sin ver lo que espera el sistema. Lo explica la ayuda de **Cierres de caja**.',
          'Si ya declaraste y falta terminar, el botón dice **Terminar cierre**. Mientras tanto la caja no vende.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Reglas',
        items: [
          'El cajero sin caja abierta no vende ni cobra. La caja también se abre desde **Ventas (POS)**.',
          'Un cajero tiene una sola caja abierta. Una caja abierta por otro cajero no se puede abrir.',
          'De la caja solo sale **vuelto** y **retiro parcial**. Gastos, pagos a proveedores y reintegros no se pagan de la caja.',
          'Una caja que quedó abierta de otro día se cuenta y se cierra, pero no vende.',
          'La caja es de la sucursal donde se abrió. Si la dueña cambia de sucursal sin cerrarla, sale el aviso **Tienes abierta la Caja … de …** con el botón **Cambiar a …**: se cuenta y se cierra operando esa sucursal.',
        ],
      },
      { tipo: 'nota', texto: 'Si hay retiros esperando, la dueña los ve arriba, en **Retiros de caja por confirmar**, aunque no haya elegido sucursal.', permiso: { clave: 'caja.confirmar-retiro' } },
    ],
    mensajes: [
      { mensaje: 'Debes abrir tu sesión de caja antes de vender o cobrar', que_hacer: 'Abre tu caja aquí, con **Abrir caja**.' },
      { mensaje: 'Esa caja ya tiene una sesión abierta por otro cajero', que_hacer: 'Elige otra caja, o que quien la tiene la cierre.' },
      { mensaje: 'Ya tienes una sesión de caja abierta: ciérrala antes de abrir otra', que_hacer: 'Ya tienes otra caja abierta en esta sucursal: cuéntala y ciérrala antes de abrir otra.' },
      { mensaje: 'Tu caja se abrió el … y sigue abierta: ciérrala antes de vender hoy', que_hacer: 'Cuéntala y ciérrala con **Contar y cerrar caja**. Después abre la de hoy.' },
      { mensaje: 'Tu caja ya declaró su conteo: termina el cierre (o pide volver a contar)', que_hacer: 'Pulsa **Terminar cierre**. Si te equivocaste al contar, pide a quien autoriza cierres que te deje volver a contar.' },
      { mensaje: 'La caja superó el límite de efectivo y ya se dieron todos los avisos', que_hacer: 'La caja no cobra ni vende hasta que registres el **Retiro parcial**.' },
      { mensaje: 'Ya tienes abierta la Caja … de …: entra a esa sucursal y ciérrala antes de abrir otra', que_hacer: 'Cierra primero la que está abierta, operando su sucursal.' },
      { mensaje: 'Tienes abierta la Caja … de …', que_hacer: 'Pulsa **Cambiar a …** en el aviso y allá pulsa **Contar y cerrar caja**.' },
      { mensaje: 'La caja está desactivada', que_hacer: 'La dueña la activa en **Configuración**, en **Sucursales, almacenes y cajas**.' },
      { mensaje: 'Estás en la consola global', que_hacer: 'Pulsa **Operar …** en la misma pantalla, o **Elegir sucursal** en la cabecera.' },
    ],
    manual: '3',
    relacionados: ['cierres', 'pos', 'notificaciones'],
    claves: ['abrir caja', 'fondo', 'fondo fijo', 'turno', 'gaveta', 'efectivo', 'sesión de caja', 'cerrar caja'],
  },
  {
    id: 'caja.abrir',
    padre: 'caja',
    titulo: 'Abrir caja',
    resumen: 'Eliges tu caja y confirmas el fondo con que empiezas. Se abre igual desde **Caja** o desde el punto de venta, sin perder lo que tienes en el carrito.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Abrir',
        pasos: [
          'Elige la **Caja** (por ejemplo Caja 1).',
          'Debajo sale el fondo fijo de esa caja, y **Fondo inicial $** y **Fondo inicial Bs** vienen con ese monto.',
          'Cuenta lo que hay en la gaveta. Si es otra cosa, escríbelo: el aviso se pone ámbar y dice que se avisará a la dueña.',
          'Pulsa **Abrir caja** (en el punto de venta, **Abrir caja y empezar a vender**).',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'El fondo fijo de cada caja lo pone la dueña en **Configuración**. El fondo no es venta: el cierre lo muestra aparte.',
          'Si la caja emite solo facturas, el aviso lo dice: «Esta caja emite solo facturas».',
          'Cada apertura del día es un **turno** nuevo de esa caja (1, 2…).',
          'Si abriste con otro monto, la tarjeta lo recuerda: «Abriste con un fondo distinto al fijo de la caja».',
          'Si dos personas pulsan **Abrir caja** a la vez sobre la misma caja, una la abre y la otra recibe el aviso de que ya está abierta.',
        ],
      },
    ],
    mensajes: [
      { mensaje: 'Esa caja ya tiene una sesión abierta por otro cajero', que_hacer: 'Elige otra caja, o que quien la tiene la cierre.' },
      { mensaje: 'Ya tienes una sesión de caja abierta: ciérrala antes de abrir otra', que_hacer: 'Un cajero tiene una sola caja abierta: cierra primero la que tienes.' },
      { mensaje: 'Ya tienes abierta la Caja … de …: entra a esa sucursal y ciérrala antes de abrir otra', que_hacer: 'Cierra primero la que está abierta, operando su sucursal.' },
      { mensaje: 'La sucursal … está desactivada: no se puede abrir caja en ella. Pide a la dueña que la active o que te asigne otra sucursal.', que_hacer: 'Una sucursal desactivada no vende: pídeselo a la dueña.' },
    ],
    manual: '3.1',
    claves: ['abrir caja', 'fondo inicial', 'fondo fijo', 'turno'],
  },
  {
    id: 'caja.retiro',
    padre: 'caja',
    titulo: 'Retiro parcial de efectivo',
    resumen: 'Sacas efectivo de tu caja durante el turno, lo entregas y lo registras. La caja sigue abierta y al cerrar ya no se cuenta ese dinero. No es un gasto.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Registrar un retiro',
        pasos: [
          'En **Caja**, pulsa **Retiro parcial** (o **Registrar retiro** en el aviso del límite de efectivo).',
          'Elige la **Moneda**: **Efectivo $** o **Efectivo Bs**.',
          'Escribe el **Monto retirado**.',
          'En **Se entrega a** escribe el nombre de quien lo recibe o la caja a la que pasa. La **Nota** es opcional.',
          'Pulsa **Registrar retiro**. Sale una ventana con su número y el botón **Imprimir comprobante**. Se queda en pantalla hasta que pulses **Listo**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'El límite de efectivo',
        items: [
          'Cuando el efectivo cobrado, sin contar el fondo, pasa el límite que fijó la dueña, sale el aviso «La caja superó el límite de efectivo: haz un retiro parcial», con el número de aviso.',
          'Cada cobro cuenta un aviso. Agotados los avisos, la caja queda bloqueada: no cobra ni emite ninguna venta, tampoco a crédito, hasta registrar el retiro.',
          'El aviso no muestra montos.',
          'Con el primer aviso, a la dueña le salta en la franja roja que la caja superó el límite. Ese aviso se apaga solo cuando la caja vuelve a quedar por debajo del límite (con el retiro) o cuando se cierra.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Después',
        items: [
          'El comprobante (media carta) lleva la firma de quien entrega y de quien recibe. Se reimprime desde la lista **Retiros parciales de efectivo**, con el botón de la impresora.',
          'El retiro queda **Por confirmar** hasta que quien lo recibe lo confirma desde su usuario. Si lo registra la dueña, nace **Confirmado**.',
          'Si quien lo recibe dice que no cuadra, queda **Observado** y te llega el aviso.',
          'Con el conteo ya declarado no se hacen retiros: el botón desaparece.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Retiro sobre la caja de otro cajero',
        permiso: { clave: 'caja.cerrar-con-diferencia' },
        pasos: [
          'En **Sesiones de la sucursal**, pulsa **Retiro** en la caja abierta de otro cajero. Sale solo si esa caja no ha declarado su conteo.',
          'Llena el retiro igual que uno propio y pulsa **Registrar retiro**.',
          'El que registra la dueña nace **Confirmado**; el que registra un administrador lo confirma la dueña. El cajero de esa caja lo ve en su lista y lo puede reimprimir.',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'Un retiro registrado no se modifica ni se borra. Revisa el monto antes de pulsar **Registrar retiro**.' },
    ],
    mensajes: [
      { mensaje: 'No tienes una caja abierta de la cual retirar', que_hacer: 'El retiro sale de tu caja abierta: ábrela primero.' },
      { mensaje: 'Esa caja ya declaró su conteo: termina el cierre', que_hacer: 'Con el conteo declarado ya no se retira: termina el cierre.' },
    ],
    manual: '3.3',
    claves: ['retiro', 'sacar efectivo', 'límite de efectivo', 'caja bloqueada', 'comprobante de retiro', 'resguardo'],
  },
  {
    id: 'caja.confirmar-retiros',
    padre: 'caja',
    titulo: 'Confirmar retiros de caja',
    permiso: { clave: 'caja.confirmar-retiro' },
    resumen: 'La dueña confirma desde su usuario que recibió el efectivo de cada retiro, o lo deja observado si no cuadra. No hace falta elegir sucursal.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Confirmar',
        pasos: [
          'En **Caja**, arriba, mira **Retiros de caja por confirmar**. Cada fila dice la caja, el cajero, el monto y a quién se entregó.',
          'Si recibiste el dinero, pulsa **Lo recibí**. El retiro queda **Confirmado**.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Si no cuadra',
        pasos: ['Pulsa **No cuadra**.', 'En **Qué no cuadra** escribe qué recibiste o qué falta.', 'Pulsa **Dejar observado**. El retiro queda **Observado** y el cajero recibe el aviso.'],
      },
      {
        tipo: 'lista',
        titulo: 'Reglas',
        items: [
          'Nadie confirma un retiro que registró él mismo: en esos no salen los botones.',
          'El retiro por confirmar te salta en la franja roja, con sonido, en la pantalla en que estés.',
          'Si un retiro es por más efectivo del que la caja tiene registrado, recibes además un aviso para revisarlo antes de confirmar.',
          'En **Cierres de caja**, la caja con retiros pendientes lo dice: «retiro(s) sin confirmar».',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'Confirmar o dejar observado no se deshace.' },
    ],
    mensajes: [
      { mensaje: 'El retiro lo confirma una persona distinta de quien lo registró', que_hacer: 'Lo confirma otra persona con el permiso.' },
      { mensaje: 'Ese retiro ya fue revisado', que_hacer: 'Otra persona ya lo confirmó o lo observó. Actualiza la pantalla.' },
    ],
    manual: '3.3',
    relacionados: ['notificaciones'],
    claves: ['confirmar retiro', 'lo recibí', 'no cuadra', 'observado', 'retiro por confirmar'],
  },

  // ─── Cierres de caja ────────────────────────────────────────────────────
  {
    id: 'cierres',
    titulo: 'Cierres de caja',
    rutas: ['/cierres'],
    permiso: { clave: 'caja.abrir' },
    resumen: 'El cierre de cada turno de caja, a ciegas: el cajero cuenta y declara sin ver lo que espera el sistema. Cada cierre deja dos reportes, el FISCAL y el COMPLETO.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Cerrar tu caja',
        pasos: [
          'Pulsa **Cerrar mi caja** (arriba a la derecha) o **Contar y cerrar** en la fila de tu caja. También desde **Caja**, con **Contar y cerrar caja**.',
          'Cuenta, escribe lo que tienes en cada método y pulsa **Declarar conteo** (paso 1).',
          'Si cuadra, la caja queda cerrada y sale la ventana con **Imprimir reportes**.',
          'Si no cuadra, escribe la justificación y pulsa **Cerrar caja con la diferencia** (paso 2).',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'La lista',
        items: [
          'Las últimas 50 sesiones de caja: caja y turno, cajero, apertura y cierre. Un cajero ve solo las suyas; el administrador y la dueña, las de toda la sucursal.',
          '**Fiscal**, **Completo**, **Cobrado** y **Diferencia** salen cuando la caja ya cerró. **Cobrado** es lo cobrado sin el vuelto, como en los reportes. La diferencia sale en rojo si la hubo.',
          'Cuando la lista trae cajas de varias sucursales (la dueña sin sucursal elegida), sale además la columna **Sucursal**.',
          'Si un faltante y un sobrante se compensan y la diferencia da cero, debajo dice «… entre métodos»: hubo diferencia aunque el neto sea cero.',
          'El estado: **Abierta**, **Conteo declarado** o **Cerrada**, con marcas como «recontada», «reabierta» o «retiro(s) sin confirmar».',
          'El botón de cada fila: **Contar y cerrar** o **Terminar cierre** en tu caja, **Reporte** en una cerrada. Quien autoriza cierres ve además **Ver** en una caja abierta de otro y **Revisar diferencia** en una que declaró y espera.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Lo que ve quien autoriza cierres',
        permiso: { clave: 'caja.cerrar-con-diferencia' },
        items: [
          '**Ver**: la vista en vivo de una caja abierta de otro cajero. El cajero no ve esos montos.',
          '**Contar y cerrar esta caja**, dentro de **Ver**: para cuando el cajero se fue sin cerrar.',
          '**Revisar diferencia**: la caja que ya declaró y espera tu autorización.',
          'La tarjeta **Cierre de sucursal**, al pie, para cerrar el día.',
        ],
      },
      { tipo: 'nota', texto: 'Un cajero no ve los cierres de las cajas de otros cajeros, ni abiertas ni cerradas: solo los de su caja.' },
      {
        tipo: 'nota',
        texto: 'Desde la consola global solo miras. Para declarar un conteo, cerrar una caja, permitir volver a contar o reabrir, elige primero en la cabecera la sucursal de esa caja.',
        permiso: { clave: 'reportes.consolidado' },
      },
    ],
    mensajes: [
      { mensaje: 'Declara lo contado en: … (escribe 0 si no hubo)', que_hacer: 'Escribe todos los métodos, con 0 donde no hubo.' },
      { mensaje: 'Para operar un cierre de caja elige primero la sucursal (desde la consola global solo se mira)', que_hacer: 'Elige la sucursal de esa caja en la cabecera y vuelve a pulsar el botón.' },
      { mensaje: 'El conteo de esta caja ya se declaró y no se cambia', que_hacer: 'Lo declarado no se edita. Si hubo un error, quien autoriza cierres puede permitir volver a contar.' },
      { mensaje: 'Hay una diferencia de arqueo de $ …: indica la justificación', que_hacer: 'Escribe la **Justificación de la diferencia (obligatoria)**.' },
      { mensaje: 'Esta caja tuvo movimiento: ciérrala desde Cierres de caja declarando lo contado por método', que_hacer: 'Ciérrala contando: **Contar y cerrar**.' },
      { mensaje: 'No se pudo abrir el cierre de esta caja: …', que_hacer: 'El mensaje dice por qué. Si dice que la caja no es de la sucursal que operas, cambia de sucursal para contarla y cerrarla.' },
    ],
    manual: '9',
    relacionados: ['caja', 'autorizacion', 'autorizaciones', 'impresion'],
    claves: ['cierre', 'cerrar caja', 'arqueo', 'conteo a ciegas', 'cuadre', 'faltante', 'sobrante', 'reporte fiscal', 'reporte completo'],
  },
  {
    id: 'cierres.conteo',
    padre: 'cierres',
    titulo: 'Paso 1: contar y declarar',
    resumen: 'Cuentas lo que tienes en la caja y lo escribes método por método, sin ver lo que espera el sistema. Lo declarado queda guardado y no se cambia.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Declarar el conteo',
        pasos: [
          'Escribe lo que contaste en **cada** método: **Efectivo $**, **Efectivo Bs**, **Zelle**, **USDT** y, si la dueña lo tiene habilitado, **Transferencia** y **Pago móvil**. Donde no hubo nada, escribe 0.',
          'En efectivo cuenta también el fondo de la caja: la pantalla te recuerda cuánto es.',
          'No cuentes los retiros que ya entregaste: la pantalla los lista aparte para recordarlo.',
          '**Punto de venta**: un renglón por cada lote que cerraste en el punto, con su **N° de lote** y su **Monto Bs**. **Otro lote** agrega un renglón. Si no pasaste tarjetas, déjalo vacío.',
          'La **Observación** es opcional. Quien revisa el cierre la ve en el arqueo como **Observación del conteo**, sale en la hoja impresa y queda en la auditoría.',
          'Pulsa **Declarar conteo**. Sale el aviso «Revisa lo escrito: después de declarar no se puede cambiar».',
          'Pulsa **Corregir** para volver, o **Sí, declarar** para confirmar.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Qué pasa después',
        items: [
          'Mientras falte un método, **Declarar conteo** no se activa y la pantalla dice cuál falta.',
          'Desde que declaras, la caja no vende ni cobra.',
          '**Si cuadra**, la caja queda cerrada en ese mismo paso y sale la ventana con **Imprimir reportes**.',
          '**Si no cuadra**, aparecen los reportes y el arqueo: sigue el paso 2.',
          'Las ventas en espera se descartan al cerrar: la pantalla dice cuántas hay.',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'Lo declarado no se edita. Si te equivocaste al escribir, solo quien autoriza cierres puede permitir volver a contar.' },
    ],
    mensajes: [
      { mensaje: 'Declara lo contado en: … (escribe 0 si no hubo)', que_hacer: 'Escribe todos los métodos, con 0 donde no hubo.' },
      { mensaje: 'Hay un número de lote repetido', que_hacer: 'Cada lote del punto va una sola vez.' },
    ],
    manual: '9.1',
    claves: ['contar', 'declarar', 'conteo a ciegas', 'lote', 'punto de venta', 'fondo', 'cerrar caja'],
  },
  {
    id: 'cierres.diferencia',
    padre: 'cierres',
    titulo: 'Paso 2: justificar la diferencia',
    resumen: 'Si el conteo no cuadra, se ven los reportes y el arqueo por método. Para cerrar se escribe la justificación y, si la diferencia pasa del umbral, se pide autorización.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'El arqueo',
        items: [
          '**Arqueo por método**: lo que espera el **Sistema**, lo que declaró el **Cajero** y la **Diferencia** de cada método.',
          'En efectivo el sistema parte del fondo y ya descuenta vueltos y retiros parciales.',
          'La **Diferencia total** va en dólares.',
          'Un faltante en un método no se compensa con un sobrante en otro. Cuando hay las dos cosas, sale «por método suman $ …»: esa suma, sin compensar, es la que se compara con el umbral.',
          'Si la suma pasa del umbral (5 $ al instalar), sale la marca «supera el umbral de $ …: requiere autorización».',
        ],
      },
      { tipo: 'nota', texto: 'Los bolívares de la diferencia se llevan a dólares con la tasa 2 del cierre. Solo tú ves esa tasa.', permiso: VE_TASA_2 },
      {
        tipo: 'pasos',
        titulo: 'Cerrar con la diferencia',
        pasos: [
          'Escribe la **Justificación de la diferencia (obligatoria)**. La **Observación** del paso 1 se conserva: no hace falta repetirla.',
          'Pulsa **Cerrar caja con la diferencia**.',
          'Si la suma pasa del umbral, sale **Requiere autorización**: la da quien tiene el permiso (la ventana dice quién), con su clave o a distancia.',
          'Al quedar cerrada sale la ventana con **Imprimir reportes**.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Permitir volver a contar',
        permiso: { clave: 'caja.cerrar-con-diferencia' },
        pasos: [
          'Si el cajero se equivocó al escribir, pulsa **Permitir volver a contar**.',
          'Escribe el **Motivo para volver a contar** y pulsa **Permitir**.',
          'El conteo anterior, con su observación, queda en auditoría, la dueña recibe el aviso y la caja vuelve a operar hasta que se declare de nuevo. La observación se va con el conteo.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Reglas',
        items: [
          'Autoriza quien tiene el permiso y no es el cajero de esa caja. Un administrador que cobra en su propia caja es un cajero más: su diferencia la autoriza otro. La dueña siempre puede.',
          'Toda diferencia declarada le llega a la dueña como alerta, pase o no del umbral.',
        ],
      },
    ],
    mensajes: [
      { mensaje: 'Hay una diferencia de arqueo de $ …: indica la justificación', que_hacer: 'Escribe la **Justificación de la diferencia (obligatoria)**.' },
      { mensaje: 'El cierre con diferencia lo autoriza otra persona', que_hacer: 'Pide la autorización a otro administrador o a la dueña.' },
      { mensaje: 'Volver a contar lo permite quien autoriza los cierres, y no en su propia caja', que_hacer: 'Lo permite otra persona con el permiso, o la dueña.' },
    ],
    manual: '9.2',
    relacionados: ['autorizacion'],
    claves: ['diferencia', 'faltante', 'sobrante', 'justificación', 'umbral', 'volver a contar', 'reconteo', 'arqueo'],
  },
  {
    id: 'cierres.reporte',
    padre: 'cierres',
    titulo: 'Los reportes del cierre',
    resumen: 'Cada cierre deja dos reportes y el arqueo por método. Se ven al declarar, en una caja cerrada con **Reporte**, y se imprimen con **Imprimir reportes** en hoja carta.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Los dos reportes',
        items: [
          '**Reporte FISCAL**: solo facturas, notas de crédito y notas de débito. Base, IVA, total, montos por método y los números emitidos.',
          '**Reporte COMPLETO**: facturas, notas y abonos. Es el que se compara con el dinero.',
          'La base, el IVA y el total salen en dólares y, debajo, en bolívares. Si hubo ventas exentas, bajo la base sale el **exento Bs**.',
          'Los números emitidos se revisan: si falta alguno sale **SALTOS**; los anulados por hoja dañada salen como **anuladas**. Cada rango empieza en el siguiente al último emitido antes del turno: así también se ve un número perdido entre turnos.',
          'La fila **N° de control** muestra los controles que usó el turno y marca como **SALTOS** los que ningún documento ni hoja anulada usó. Solo busca saltos dentro de los talonarios cargados.',
          'Si el turno pasó de un talonario al siguiente, esa fila trae una nota como «Cambio de talonario: del 00-000500 al 00-001001»: los números entre los dos no son hojas perdidas. La nota sale también en la hoja impresa.',
          'Las hojas anuladas (en blanco o por hoja dañada) salen como **anuladas** desde el control siguiente al último documento anterior al turno: una hoja anulada entre dos turnos, o después de la última factura del día, sale en el cierre del turno siguiente. Con dos cajas que intercalan facturas puede salir en los dos cierres. Sin documentos anteriores en la sucursal, desde el comienzo del talonario.',
          'Una hoja en blanco anulada sale además en el cierre de la caja de quien la anuló, si tenía su caja abierta, aunque el turno no haya emitido documentos: la fila dice «sin emisiones» y marca la hoja como **anuladas**. La anulada sin caja abierta sale en el cierre de sucursal.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Filas aparte',
        items: [
          '**Cobrado** no suma el saldo a favor usado como pago ni el crédito de un cambio: no es dinero que entra.',
          'Si hubo vuelto, esa fila dice **Recibido (con el vuelto)**: lo que entró por cada método. Debajo sale **Vuelto entregado · cobrado sin el vuelto**.',
          'El **IGTF percibido** sale en $ y en Bs. En el FISCAL es el de los cobros de facturas, notas de crédito y notas de débito, también el de las facturas pagadas con saldo a favor (ese dinero entró como abono). El COMPLETO suma además el de los anticipos anteriores al 19/10/2026.',
          'Un abono o anticipo recibido desde el 19/10/2026 no suma IGTF: entra completo al saldo del cliente y su IGTF se cobra en la factura que se pague con él.',
          'Los **Vueltos entregados**, el **Sobrante por vuelto** y los **Retiros parciales de efectivo** salen en filas aparte.',
          'Los vueltos de pago móvil y transferencia salen del banco, no de la caja.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'La hoja impresa',
        items: [
          '**Imprimir reportes** saca en hoja carta lo mismo que la pantalla: los dos reportes con sus Bs (también el exento), el IGTF, el vuelto y lo cobrado sin el vuelto, los vueltos por medio, el sobrante, los abonos (su total y el recuadro **Abonos del turno**, uno por uno), el total de retiros, las salidas de caja anteriores al 09/10/2026 (si las hubo), el arqueo y la observación del conteo.',
          '**Abonos del turno**: cada abono a saldo a favor con su fecha y hora, cliente, recibo, forma de pago (con la referencia) y monto, y el total. Ya van sumados en el reporte COMPLETO. Sale en la pantalla y en la hoja.',
          'Desde la segunda impresión dice **REIMPRESIÓN**. La vista preliminar de una caja abierta no cuenta como impresión.',
          'Si quien la abre no puede ver ese cierre, la hoja dice «No se pudo abrir la hoja del cierre: …» con el motivo.',
        ],
      },
      { tipo: 'nota', texto: 'Si la imprimes tú, la hoja trae además la tasa 2 al cierre, en el COMPLETO; también la ves en la pantalla. Los demás no la ven.', permiso: VE_TASA_2 },
      {
        tipo: 'lista',
        titulo: 'Más abajo',
        items: [
          '**Arqueo por método**, con la justificación y quién autorizó, si hubo diferencia, y la **Observación del conteo** si el cajero escribió una.',
          '**Retiros parciales de efectivo** del turno, con su estado.',
          '**Documentos del turno por método de pago**: pulsa **Ver detalle**. Cada documento dice su total en $, con qué se pagó, cuánto y la referencia de cada pago. Lo cobrado en bolívares (punto, efectivo Bs, transferencia, pago móvil) sale en Bs; lo cobrado en divisa, en $.',
        ],
      },
      {
        tipo: 'nota',
        texto: 'En una caja abierta de otro cajero, quien autoriza cierres ve los montos en vivo. Con **Contar y cerrar esta caja** la cuenta y la cierra él, por ejemplo si el cajero se fue sin cerrar.',
        permiso: { clave: 'caja.cerrar-con-diferencia' },
      },
    ],
    manual: '9.3',
    relacionados: ['impresion'],
    claves: ['reporte fiscal', 'reporte completo', 'saltos', 'imprimir cierre', 'IGTF percibido', 'vueltos'],
  },
  {
    id: 'cierres.sucursal',
    padre: 'cierres',
    titulo: 'Cierre de sucursal',
    permiso: { clave: 'caja.cerrar-con-diferencia' },
    resumen: 'Cierra el día de la sucursal: consolida los turnos de caja de esa fecha y sus retiros, y anota las cajas que quedaron sin cerrar. Se hace una sola vez por día.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Cerrar la sucursal',
        pasos: [
          'Cierra primero todas las cajas del día: con cajas abiertas el administrador no puede cerrar la sucursal (sale «Hay … sin cerrar … ciérralas antes de cerrar la sucursal»). Solo la dueña puede cerrarla con cajas abiertas, y quedan anotadas como sin cerrar.',
          'En la tarjeta **Cierre de sucursal**, elige la **Fecha** y pulsa **Cerrar sucursal**.',
          'La ventana dice el día (DD/MM/AAAA) y revisa todos los turnos de ese día, no solo los de la lista: mientras dice «Revisando los turnos de caja de ese día…», el botón no se activa. Luego dice cuántos turnos hubo y cuáles cajas siguen sin cerrar. Si no pudo revisarlos, lo dice con el motivo.',
          'Pulsa **Sí, cerrar la sucursal**, o **Cancelar** para volver sin cerrar nada.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'La lista de cierres',
        items: [
          'Cada cierre muestra la fecha (DD/MM/AAAA), quién lo hizo, lo **Cobrado**, lo **Fiscal**, lo **No fiscal**, los **Retiros** (y cuántos siguen sin confirmar) y las **Cajas sin cerrar**. Si trae cierres de varias sucursales, sale también la columna **Sucursal**.',
          'Lo cobrado (sin el vuelto), los retiros y los retiros sin confirmar salen solo de las cajas cerradas. Una caja abierta, o reabierta, sale con todo en cero: queda anotada como sin cerrar.',
          'Lo emitido ese día sin caja abierta no está en ningún cierre de caja. El cierre de sucursal lo muestra aparte, con su monto: «doc. fiscal(es) fuera de caja» (por ejemplo una nota de crédito) y «cambio(s) o nota(s) fuera de caja».',
          'Las hojas en blanco anuladas ese día sin caja abierta salen como «Hojas anuladas fuera de caja: …», con sus números de control.',
          'Si la dueña reabre un turno de ese día después del cierre de sucursal, el cierre no se rehace: bajo **Cajas sin cerrar** sale «Turno reabierto: Caja … · cajero: antes $ …, ahora $ …» (o «sigue abierto» mientras no se vuelva a cerrar).',
          'Arriba de la lista, **Desde** y **Hasta** la acotan por la fecha del cierre (**Hoy** pone el día de hoy y **×** quita las fechas). Abajo dice cuántos cierres hay; salen 25 por página y, si hay más, **Anterior** y **Siguiente** pasan de página.',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'Solo hay un cierre de sucursal por día: no se repite ni se deshace.' },
    ],
    mensajes: [
      { mensaje: 'Ya existe el cierre de sucursal del …', que_hacer: 'Ese día ya se cerró. No se repite.' },
      { mensaje: 'No hay sesiones de caja abiertas el …', que_hacer: 'Ese día no se abrió ninguna caja: revisa la fecha.' },
    ],
    manual: '9.5',
    claves: ['cierre de sucursal', 'cierre del día', 'cajas sin cerrar', 'fuera de caja'],
  },
  {
    id: 'cierres.reabrir',
    padre: 'cierres',
    titulo: 'Reabrir una caja cerrada',
    permiso: { clave: 'caja.reabrir' },
    resumen: 'La dueña vuelve a abrir el turno de una caja ya cerrada, con un motivo. El cierre anterior queda guardado en auditoría.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Reabrir',
        pasos: ['En la fila de la caja cerrada, pulsa **Reabrir**.', 'Escribe el **Motivo** (al menos 5 letras).', 'Pulsa **Reabrir**.'],
      },
      {
        tipo: 'lista',
        titulo: 'Qué pasa',
        items: [
          'La caja vuelve a estar abierta y lo declarado antes deja de valer, con su observación: al cerrarla se cuenta y se declara de nuevo.',
          'El cierre anterior, con su conteo, su justificación y su observación, queda en **Auditoría**.',
          'El cajero recibe en la campana el aviso de que su caja se reabrió, con el motivo. Si ese día ya tenía cierre de sucursal, el aviso lo dice: ese cierre no cambia.',
          'La fila muestra cuántas veces se reabrió: «reabierta ×1».',
        ],
      },
      { tipo: 'nota', texto: 'Lo que dice un cierre emitido (la diferencia, la justificación, quién la aprobó, quién cerró, cuándo y la observación) no se cambia sin reabrir la caja.' },
    ],
    mensajes: [
      { mensaje: 'La caja o el cajero ya tienen otra sesión abierta: ciérrala antes de reabrir esta', que_hacer: 'Cierra primero la otra sesión de esa caja o de ese cajero.' },
      { mensaje: 'Para operar un cierre de caja elige primero la sucursal (desde la consola global solo se mira)', que_hacer: 'Elige en la cabecera la sucursal de esa caja y pulsa **Reabrir** otra vez.' },
    ],
    manual: '9.6',
    claves: ['reabrir', 'reapertura', 'caja cerrada'],
  },

  // ─── Autorizaciones ─────────────────────────────────────────────────────
  {
    id: 'autorizaciones',
    titulo: 'Autorizaciones',
    rutas: ['/autorizaciones'],
    permiso: { clave: 'autorizaciones.resolver', nivel: 'total' },
    resumen: 'La bandeja de quien aprueba: las solicitudes a distancia que puedes aprobar o negar, y el historial de todo lo autorizado, con clave o a distancia.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Aprobar una solicitud',
        pasos: ['En **Pendientes**, lee qué se pide, quién lo pide y de qué sucursal, el motivo, el monto y los datos de la operación.', 'Pulsa **Aprobar**. La pantalla de quien la pidió sigue sola.'],
      },
      {
        tipo: 'pasos',
        titulo: 'Negar una solicitud',
        pasos: ['Pulsa **Negar**.', 'Escribe **Por qué se niega**. Ese motivo le llega a quien la pidió.', 'Pulsa **Negar** en la ventana.'],
      },
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          '**Pendientes** se actualiza sola cada 10 segundos. Cada solicitud dice cuándo se pidió y cuándo vence.',
          'Una solicitud sin respuesta vence y hay que pedirla de nuevo. Una aprobada que nadie usa a tiempo también vence: en el **Historial** pasa a **Vencida** y conserva quién la aprobó.',
          'Una solicitud que pediste tú sale sin botones: la resuelve otra persona.',
          '**Historial**: qué se autorizó, quién lo pidió, quién autorizó, la **Vía** (**Clave** o **A distancia**) y el estado: **Pendiente**, **Aprobada**, **Usada**, **Negada** o **Vencida**. Se filtra por estado y por fechas.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Reglas',
        items: [
          'Ves las solicitudes de lo que tu usuario puede autorizar, en tu sucursal.',
          'Nadie se autoriza a sí mismo.',
          'Cada autorización se usa una sola vez y vale por el monto aprobado.',
          'Toda aprobación que no dé la dueña le llega a ella como alerta.',
        ],
      },
      {
        tipo: 'nota',
        texto: 'Desde la consola global, sin elegir sucursal, ves y apruebas las solicitudes de todas las sucursales, estés donde estés. Operando una sucursal, solo las de esa: la de la otra no sale en **Pendientes** ni se aprueba desde ahí. Para aprobarla, vuelve a la consola global con **Elegir sucursal** o cambia a esa sucursal. El aviso dice de qué sucursal es: «… (Barinas) pide autorización: …».',
        permiso: { clave: 'reportes.consolidado' },
      },
    ],
    mensajes: [
      { mensaje: 'La autorización no existe', que_hacer: 'Si eres la dueña y estás operando otra sucursal, vuelve a la consola global (o cambia a la sucursal de la solicitud) y apruébala desde ahí.' },
      { mensaje: 'La solicitud ya fue aprobada por …', que_hacer: 'Otra persona la resolvió primero. No hay que hacer nada.' },
      { mensaje: 'La solicitud venció sin respuesta: debe pedirse de nuevo', que_hacer: 'Quien la necesita tiene que pedirla otra vez.' },
      { mensaje: 'No puedes resolver una autorización que pediste tú', que_hacer: 'La resuelve otra persona con el permiso.' },
      { mensaje: 'Tu usuario no puede autorizar: …', que_hacer: 'La autoriza quien tiene ese permiso, o la dueña.' },
    ],
    manual: '10.4',
    relacionados: ['autorizacion', 'notificaciones'],
    claves: ['aprobar', 'negar', 'solicitud', 'a distancia', 'bandeja', 'permiso', 'autorizar'],
  },

  // ─── Consolidado ────────────────────────────────────────────────────────
  {
    id: 'consolidado',
    titulo: 'Consola consolidada',
    rutas: ['/consolidado'],
    permiso: { clave: 'reportes.consolidado' },
    resumen: 'La vista global de la dueña: todas las sucursales a la vez y la elección de la sucursal que va a operar. Si entras sin sucursal elegida, el sistema te trae aquí.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          '**¿Qué sucursal vas a operar?**: una tarjeta por sucursal con sus ventas de hoy y de la semana, sus cajas abiertas y los botones **Operar** y **Ver detalle**. Debajo del título dice dónde estás: en la consola global, o «Estás operando …» si ya operas una sucursal.',
          'Las cifras **Ventas de hoy**, **Semana**, **Cajas abiertas ahora** (con las entregas pendientes) y **Alertas** (con las cuentas por cobrar y por pagar).',
          '**Comparativo por sucursal**: ventas de hoy y de la semana, cajas abiertas (caja, cajero, desde qué hora y cuánto lleva), alertas, pendientes de entrega, cuentas por cobrar y por pagar, y el botón **Detalle**.',
          '**Ganancia real** entre dos fechas y **Productos con más ventas en $ de la semana · márgenes** (como el resto del tablero, sin las sucursales desactivadas).',
          'Si la tasa 2 lleva más horas sin escribirse que el recordatorio (24 al instalar), sale el aviso con el botón **Actualizar tasa**. Traer la BCV no lo apaga: se apaga al registrar la tasa 2.',
          '**Configuración global**, arriba a la derecha, lleva a **Configuración**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Las alertas de cada sucursal',
        items: [
          'Productos **bajo mínimo**, medidos contra lo disponible. Cuenta también los que tienen algún depósito bajo su mínimo.',
          '**Retenciones** con más de 30 días y **diferencia de arqueo** del día.',
          '**Ajustes** y **gastos** por aprobar.',
          '**Traslados** en camino y **diferencias de traslado** sin revisar.',
          'Material **sin retirar** pasado el plazo.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Ganancia real',
        items: [
          'La cuenta en escalera: **Ventas** − **Costo de lo vendido** = **Margen bruto**; menos los gastos generales sin factura y los operativos con factura = **Ganancia real**. Cada paso dice su % de las ventas.',
          'Al lado: **De cada $ 100 vendidos quedan $ X** (o «se pierden»).',
          'Período: **Hoy**, **Últimos 7 días**, **Este mes** o **Mes pasado**, o escribe **Desde** y **Hasta**.',
          'El aviso **Hay ventas sin costo cargado** dice cuánto se vendió de productos sin costo: su costo cuenta como $ 0 y la ganancia sale más alta de lo real. En **Productos con más ventas**, esos dicen **sin costo** (o **parte sin costo**).',
          'Las ventas ya descuentan el **Desc. %** que se puso a toda la venta. Los productos con más ventas también.',
          'El filtro **Gastos** separa **Todos**, **De la tienda** o **No de la tienda**.',
          'Los gastos asociados a productos no se restan aquí: ya van en el costo de lo vendido.',
          'Es información interna, no fiscal.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Operar una sucursal',
        pasos: [
          'Pulsa **Operar** en la tarjeta de la sucursal.',
          'Deja el motivo que viene o escribe otro, y pulsa **Operar …** con el nombre de la sucursal.',
          'Trabajas como si fueras de esa sucursal. Arriba sale una franja ámbar que dice que operas como superadmin y que toda acción queda auditada.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Cambiar de sucursal o volver',
        items: [
          'En la cabecera, **Elegir sucursal** (o el nombre de la sucursal que operas) abre la lista: eliges otra, escribes el motivo y quedas operándola.',
          '**Volver a la consola global**, en esa lista, o **Salir del contexto**, en la franja, te devuelven aquí.',
          'Si entras a **Ventas (POS)**, a **Cotizaciones** o a **Caja** sin sucursal, la misma pantalla ofrece los botones **Operar …** y te deja ahí mismo.',
          'Para aprobar autorizaciones y confirmar retiros de caja no hace falta elegir sucursal: desde la consola global apruebas las de todas. Operando una sucursal, solo las de esa.',
        ],
      },
      { tipo: 'nota', texto: 'Operar una sucursal dura un tiempo limitado. Al vencer, el sistema avisa y vuelves a la consola global. Si recargas la página sigues en la misma sucursal; al cerrar la pestaña hay que elegirla de nuevo.' },
      { tipo: 'nota', texto: '**Ver detalle** y **Detalle** abren los reportes de esa sucursal sin operarla. Al abrir el consolidado se generan los avisos que falten de facturas vencidas y de material sin retirar.' },
      { tipo: 'nota', texto: 'El menú y el tablero piden el mismo permiso, «Consolidado de todas las sucursales»: por plantilla lo tiene solo la dueña, y ella puede dárselo a otro usuario. Operar una sucursal sigue siendo solo de la dueña.' },
    ],
    mensajes: [
      { mensaje: 'Tu operación en … venció', que_hacer: 'Elige de nuevo la sucursal en la cabecera.' },
      { mensaje: 'Estás en la consola global', que_hacer: 'Pulsa **Operar …** en la misma pantalla, o **Elegir sucursal** en la cabecera.' },
    ],
    manual: '11',
    relacionados: ['configuracion', 'reportes', 'autorizaciones'],
    claves: ['dueña', 'superadmin', 'todas las sucursales', 'elegir sucursal', 'operar', 'consola global', 'ganancia real', 'márgenes', 'contexto', 'tablero'],
  },
  {
    id: 'consolidado.operar',
    padre: 'consolidado',
    titulo: 'Operar una sucursal',
    resumen: 'La dueña toma una sucursal para trabajar en ella como si fuera de ahí: vender, cobrar, manejar su inventario. Se pide un motivo y todo queda en auditoría.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Tomar la sucursal',
        pasos: [
          'Pulsa **Operar** en el consolidado, elige la sucursal en **Elegir sucursal** (cabecera) o pulsa **Operar …** en una pantalla que lo pida.',
          'En **Motivo** deja el que viene o escribe uno propio (al menos 3 letras). Queda en la auditoría.',
          'Pulsa **Operar …** con el nombre de la sucursal.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'Desde el consolidado o la cabecera vas al **Panel general** de esa sucursal. Desde una pantalla que pedía sucursal, te quedas en ella.',
          'Mientras operas una sucursal, el consolidado lo dice bajo **¿Qué sucursal vas a operar?**: «Estás operando …». Ahí ves todas las sucursales en solo lectura y puedes elegir otra.',
          'Dura un tiempo limitado. Al vencer, vuelves a la consola global.',
          'Al cambiar de sucursal con la lista de la cabecera, primero sales de la que operabas: si cancelas el motivo, quedas en la consola global.',
          'Salir de una sucursal corta al instante el acceso con que la operabas, también en otra pestaña que estuviera operando una sucursal.',
          'Una caja que abriste en una sucursal se cuenta y se cierra operando esa sucursal.',
        ],
      },
    ],
    mensajes: [
      { mensaje: 'Tu operación en … venció', que_hacer: 'Elige de nuevo la sucursal.' },
      { mensaje: 'Saliste de esa sucursal: vuelve a elegirla para operarla', que_hacer: 'Saliste de la sucursal en otra pestaña: elígela de nuevo en la cabecera.' },
    ],
    manual: '11.1',
    claves: ['operar', 'elegir sucursal', 'cambiar de sucursal', 'contexto', 'motivo'],
  },
  {
    id: 'consolidado.arranque',
    padre: 'consolidado',
    titulo: 'Primera instalación',
    resumen: 'Si la instalación no tiene sucursales, el consolidado muestra el **Wizard de arranque** para crear la primera, con su almacén principal y su caja.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Crear la sucursal inicial',
        pasos: [
          'Escribe **Código**, **Nombre**, **Serie fiscal (A-Z)** y **Dirección fiscal**.',
          'Elige el **Formato de impresión**: **Carta** o **Media carta**.',
          'Revisa el nombre del **Almacén principal** y de la **Caja**.',
          'Pulsa **Crear sucursal inicial**. Después crea los usuarios en **Configuración**.',
        ],
      },
      { tipo: 'nota', texto: 'En la instalación de Materiales y Decoraciones MG no hace falta: las dos sucursales, sus almacenes y sus cajas ya vienen creados.' },
    ],
    manual: '11.3',
    claves: ['wizard', 'arranque', 'primera sucursal', 'instalación'],
  },

  // ─── Configuración ──────────────────────────────────────────────────────
  {
    id: 'configuracion',
    titulo: 'Configuración',
    rutas: ['/configuracion'],
    permiso: { clave: 'usuarios.admin', nivel: 'total' },
    resumen: 'Donde la dueña ajusta el sistema: tasas, usuarios y permisos, parámetros, datos de la empresa, sucursales con sus almacenes y cajas, listas de precio, categorías y la firma de las retenciones.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Las tarjetas',
        items: [
          '**Tasas**: la BCV y la tasa 2, con su histórico.',
          '**Usuarios**: crear, cambiar la clave, dar permisos, desactivar.',
          '**Parámetros del sistema**: topes, plazos, umbrales y las leyendas de los formatos.',
          '**Datos de la empresa**: lo que sale en la cabecera de notas, cotizaciones y comprobantes.',
          '**Sucursales, almacenes y cajas**: la estructura, con **Datos y teléfonos**, **Numeración y talonario** y **Papel de factura** de cada sucursal.',
          '**Listas de precio y categorías**: los catálogos para productos y clientes.',
          '**Firma de los comprobantes de retención**.',
        ],
      },
      { tipo: 'nota', texto: 'Las tarjetas y ventanas con un **?** junto al título tienen su propia ayuda.' },
      { tipo: 'nota', texto: 'Todo cambio queda en **Auditoría** con el antes y el después. Los documentos ya emitidos no cambian.' },
    ],
    manual: '12',
    relacionados: ['auditoria', 'consolidado', 'impresion'],
    claves: ['configuración', 'ajustes del sistema', 'tasas', 'usuarios', 'parámetros', 'sucursales', 'cajas'],
  },
  {
    id: 'configuracion.tasas',
    padre: 'configuracion',
    titulo: 'Tasas',
    resumen: 'La BCV y la tasa 2 del día. Con la tasa 2 salen los bolívares de cada precio; la BCV sirve para el «$ a BCV» y para el IGTF.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Registrar las tasas',
        pasos: [
          'Los dos campos traen las tasas que rigen. Corrige encima, con coma decimal (855,66).',
          '**BCV (Bs/$) · de internet o a mano**: viene sola de internet una vez al día. También la traes con **Traer la BCV del día (internet)**.',
          '**Tasa 2 / Binance (Bs/$) · la escribe usted**: la escribes tú. El sistema no la trae de ningún lado.',
          '**Factor (calculado)** se recalcula mientras escribes: es la tasa 2 entre la BCV. Si cambia, se resalta y dice cuál rige todavía.',
          'Pulsa **Registrar tasas**. Hasta entonces nada cambia.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Qué responde «Traer la BCV del día»',
        items: [
          '**BCV traída de internet**: quedó registrada y ya rige. Dice la anterior y la fecha que da la fuente; si no es de hoy, lo advierte (el BCV no publica fines de semana ni feriados).',
          '**La BCV no cambió**: la fuente dio la misma tasa. No se registró nada.',
          '**No se pudo traer la BCV. No se cambió nada.** Dice el motivo y cuál sigue rigiendo. El cursor queda en el campo BCV: escríbela a mano y pulsa **Registrar tasas**. Queda como «manual».',
          'Una BCV con un salto de más de 20 % frente a la vigente se toma por un dato malo y no se registra. Si es la correcta, escríbela a mano.',
          'Traer la BCV nunca toca la tasa 2.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'La BCV automática y el recordatorio',
        items: [
          'La primera tasa de la instalación la registra la dueña a mano. Desde ahí la BCV se trae sola, solo si ese día nadie registró una tasa.',
          'Si la consulta falla, se reintenta cada media hora. Al tercer fallo del día te llega a la campana «La BCV de hoy no se pudo traer sola».',
          'La tasa 2 no se actualiza sola. Pasado el **Recordatorio de tasa (horas)**, la cabecera la marca en ámbar y el consolidado avisa con **Actualizar tasa**.',
          'Cada registro crea una fila en el histórico, con su hora y su **Fuente** (internet, manual o inicial). Una tasa anterior nunca se edita.',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'Los documentos ya emitidos conservan sus tasas: cambiar la tasa no los altera. Una venta que está en pantalla se recalcula con la tasa nueva.' },
    ],
    mensajes: [
      { mensaje: 'No se pudo traer la BCV. No se cambió nada.', que_hacer: 'Escribe la BCV de hoy a mano en su campo y pulsa **Registrar tasas**.' },
      { mensaje: 'La consulta trajo … y la BCV vigente es …: un salto de más de 20 % se toma por un dato malo', que_hacer: 'Revisa la BCV publicada. Si es la correcta, escríbela a mano y registra.' },
    ],
    manual: '12.1',
    relacionados: ['general'],
    claves: ['tasa', 'BCV', 'tasa 2', 'paralela', 'Binance', 'factor', 'dólar', 'cambio del día'],
  },
  {
    id: 'configuracion.usuarios',
    padre: 'configuracion',
    titulo: 'Usuarios',
    resumen: 'Las personas que entran al sistema, con su rol y su sucursal. Un usuario no se borra: se desactiva.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Crear un usuario',
        pasos: [
          'En **Usuarios**, pulsa **Nuevo**.',
          'Escribe **Nombre**, **Correo** y **Contraseña inicial** (mínimo 8 caracteres).',
          'Elige el **Rol** y la **Sucursal**. Solo la dueña y el contador pueden ir sin sucursal (**Global**). La lista solo ofrece las sucursales activas.',
          'Pulsa **Crear usuario**. Entra con lo que permite su rol; para darle o quitarle algo, usa **Permisos**.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Cambiar una clave',
        pasos: [
          'Pulsa **Clave** en la fila del usuario. Sirve cuando alguien olvidó su contraseña o hay que quitársela a otra persona.',
          'Escribe la **Clave nueva** (mínimo 8 caracteres) y pulsa **Cambiar clave**.',
          'Las sesiones abiertas de ese usuario se cortan al instante (le sale «Tu acceso cambió (clave, rol o sucursal): entra de nuevo») y, si estaba bloqueado por intentos fallidos, se desbloquea. Dísela en persona.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'La lista',
        items: [
          'Cada fila muestra el nombre y el correo, el rol, la sucursal (o **Global**), el último acceso y si está **Activo** o **Inactivo**.',
          '**Permisos**: lo que ese usuario puede hacer de más o de menos que su rol.',
          '**Desactivar** / **Activar**: **Desactivar** pide confirmar en una ventana. El usuario desactivado sale al instante y no entra, pero sigue en la lista y en la auditoría. Ya no recibe avisos. **Activar** es directo.',
          'Aquí no se cambian el nombre, el correo, el rol ni la sucursal de un usuario ya creado. Un usuario con una caja abierta no se pasa a otra sucursal: primero se cierra esa caja.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Quien recibe de la dueña el permiso de usuarios',
        items: [
          'Solo maneja los usuarios de su propia sucursal: en la lista ve solo esos (nunca a la dueña), los crea en ella, con un rol que no traiga más de lo que él mismo puede, y no los pasa a otra sucursal.',
          'No crea un usuario con rol de dueña ni toca a una dueña: ni su clave, ni su estado, ni sus datos.',
          'No se cambia su propio rol ni sus propios permisos.',
          'No da un permiso con más nivel del que él mismo tiene.',
          'Solo le cambia la clave, el estado, los datos o los permisos a un usuario cuyos permisos caben en los suyos. Si ese usuario tiene uno que él no tiene, eso lo hace la dueña.',
        ],
      },
      { tipo: 'nota', texto: 'Una sucursal desactivada no recibe usuarios activos: no se le crean, no se reactivan ni se pasan a ella.' },
      { tipo: 'nota', tono: 'aviso', texto: 'Antes de operar de verdad, cambia la clave de la dueña y desactiva los usuarios de prueba o ponles una clave nueva.' },
    ],
    mensajes: [
      { mensaje: 'Ya existe un usuario con ese correo', que_hacer: 'Usa otro correo, o busca en la lista el usuario que ya existe.' },
      { mensaje: 'El rol … requiere una sucursal asignada', que_hacer: 'Elige la **Sucursal**: solo la dueña y el contador van sin sucursal.' },
      { mensaje: 'No puedes desactivarte ni quitarte el rol superadmin a ti mismo', que_hacer: 'Tu propio usuario no se desactiva.' },
      { mensaje: 'Solo la dueña crea un usuario con rol de dueña', que_hacer: 'Pídeselo a la dueña.' },
      { mensaje: 'El usuario no existe (al cambiar a una dueña o a alguien de otra sucursal)', que_hacer: 'Con el permiso de usuarios solo cambias a los de tu sucursal que tengan permisos que tú también tienes. Lo demás se lo pides a la dueña.' },
      { mensaje: 'Solo creas usuarios de la sucursal que administras', que_hacer: 'Elige tu sucursal. Para otra sucursal, pídeselo a la dueña.' },
      { mensaje: 'No puedes dar el rol …: trae «…», que tú no tienes con ese nivel', que_hacer: 'Elige un rol que no pase de lo que tú puedes, o pídeselo a la dueña.' },
      { mensaje: 'No puedes cambiar la clave, el estado ni los datos de …: tiene «…», que tú no tienes con ese nivel. Eso lo hace la dueña.', que_hacer: 'Ese usuario tiene un permiso que tú no tienes: pídeselo a la dueña.' },
      { mensaje: 'La sucursal … está desactivada: no se le crean ni se le activan usuarios. Actívala primero en Configuración o asigna otra sucursal.', que_hacer: 'Activa la sucursal, o elige otra.' },
      { mensaje: '… tiene abierta la … de …: ciérrala antes de pasarlo a otra sucursal', que_hacer: 'Ese usuario tiene una caja abierta: que la cuente y la cierre antes de cambiarlo de sucursal.' },
    ],
    manual: '12.2',
    relacionados: ['login'],
    claves: ['usuario', 'nuevo usuario', 'contraseña', 'clave', 'olvidó', 'bloqueado', 'desactivar', 'rol'],
  },
  {
    id: 'configuracion.permisos',
    padre: 'configuracion',
    titulo: 'Permisos por usuario',
    resumen: 'Cada usuario parte de la plantilla de su rol. Aquí le das o le quitas permisos, uno por uno. El cambio rige de inmediato.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Cambiar los permisos de un usuario',
        pasos: [
          'En **Usuarios**, pulsa **Permisos** en la fila del usuario.',
          'Cada permiso tiene dos columnas: **Plantilla del rol** y **Este usuario**. Busca con **Buscar permiso…**.',
          'En **Este usuario** elige el nivel: **No**, **Solo ver**, **Propone** o **Sí (total)**.',
          'Las filas distintas a la plantilla se resaltan y arriba se cuentan. **Volver a la plantilla** pone todas las filas como en la plantilla.',
          'Pulsa **Guardar permisos**. Hasta entonces no cambia nada.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Los niveles',
        items: [
          '**No**: no tiene acceso.',
          '**Solo ver**: consulta, sin cambiar nada.',
          '**Propone**: carga, pero otro aprueba (se usa en los ajustes de inventario).',
          '**Sí (total)**: puede hacerlo.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'El usuario no tiene que salir y volver a entrar: el servidor aplica el cambio desde su siguiente acción. Su menú y sus botones se ponen al día cuando su acceso se renueva solo (a lo sumo en 15 minutos) o cuando recarga la página.',
          'Un permiso dado a un usuario le sirve en su sucursal.',
          'Quien recibe el permiso de una operación que pide autorización también puede autorizarla a otros.',
          'Los permisos de la dueña no se editan: siempre los tiene todos.',
          'Quien recibió el permiso de usuarios sin ser la dueña no cambia sus propios permisos, no da un permiso con más nivel del que él mismo tiene y no toca los permisos de un usuario que tiene alguno que él no tiene.',
          'Cada cambio queda en **Auditoría**.',
          'La lista trae solo los permisos que hacen algo: «Cobros: liberar una referencia» y «Libros: editar retenciones» ya no salen (corregir un comprobante depende de «Retenciones: corregir comprobantes»).',
          'El menú **Reportes** se ve con cualquiera de los tres permisos de reportes, y cada pestaña pide el suyo. El menú **Consolidado** pide «Consolidado de todas las sucursales», lo mismo que su tablero.',
          'Cuando a alguien le falta un permiso, el mensaje lo nombra como sale en esta lista: «Tu usuario no tiene el permiso «…». Si lo necesitas, pídeselo a la dueña.».',
        ],
      },
    ],
    mensajes: [
      { mensaje: 'La dueña (superadmin) siempre tiene todos los permisos: no se editan', que_hacer: 'No hace falta: la dueña ya puede todo.' },
      { mensaje: 'No puedes cambiar los permisos de …: tiene «…», que tú no tienes con ese nivel. Eso lo hace la dueña.', que_hacer: 'Ese usuario tiene un permiso que tú no tienes: sus permisos los cambia la dueña.' },
      { mensaje: 'No puedes cambiar tus propios permisos: los da la dueña', que_hacer: 'Pídele a la dueña que te los cambie.' },
      { mensaje: 'No puedes dejarle «…» con ese nivel: tú no lo tienes. Eso lo hace la dueña.', que_hacer: 'Deja ese permiso en un nivel que tú tengas, o pídele a la dueña que lo dé.' },
    ],
    manual: '13',
    relacionados: ['autorizacion', 'general'],
    claves: ['permisos', 'plantilla', 'rol', 'dar permiso', 'quitar permiso', 'acceso', 'sin acceso'],
  },
  {
    id: 'configuracion.parametros',
    padre: 'configuracion',
    titulo: 'Parámetros del sistema',
    resumen: 'Los topes, plazos y umbrales con que trabaja el sistema, y los textos que se imprimen al pie de los formatos.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Cambiar un parámetro',
        pasos: [
          'Busca el parámetro en la lista. Debajo de su nombre dice para qué sirve y quién lo cambió por última vez.',
          'Escribe el valor nuevo en su casilla, como se ve en pantalla: «1.000» es mil y «1,5» es uno y medio. La casilla muestra el valor guardado con ese mismo formato.',
          'Pulsa su **Guardar**: se activa cuando cambiaste algo. Los de sí o no, como **El cajero declara transferencias y pago móvil en el cierre**, también: marcar la casilla no guarda nada y al lado sale «(sin guardar)» hasta que pulses su **Guardar**.',
          'Los parámetros valen para toda la empresa: los cambia solo la dueña, aunque haya dado a otra persona el permiso de configuración.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Rangos',
        items: ['**Recordatorio de tasa (horas)** va de 1 a 168 (una semana).', 'Los minutos de las dos autorizaciones van de 1 a 1.440 (un día).', 'Fuera de rango no se guarda y el sistema lo dice.'],
      },
      {
        tipo: 'lista',
        titulo: 'Los que más se usan',
        items: [
          '**IVA (%)** e **IGTF sobre pagos en divisas de facturas (%)**.',
          '**Tope en Bs de una NOTA ($)** y **Notas con bolívares por cliente y día (0 = sin límite)**.',
          'El tope de la factura pagada con divisas para salir a tasa BCV (20 $ al instalar). En la lista sale con su descripción: «Factura pagada con divisas y el resto en bolívares…».',
          '**Diferencia de cierre que exige autorización ($)**, los límites de efectivo en $ y en Bs que piden retiro parcial, y **Avisos de retiro antes de bloquear la caja**.',
          'Los plazos de devolución, de crédito, de vigencia de las cotizaciones y para retirar el material.',
          '**Autorización a distancia: minutos de espera** y **Autorización con clave: minutos de validez**.',
          '**Avisar cuando al talonario fiscal le queden (hojas)** y **Recordatorio de tasa (horas)**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Leyendas de los formatos',
        items: [
          'En la misma lista están los textos que se imprimen: cambios y devoluciones, cortes de vidrio, retiro de material, precios (cotización), IGTF (cotización) e IGTF (recuadro de la factura).',
          'Una leyenda vacía no se imprime.',
        ],
      },
      { tipo: 'nota', texto: 'Cada cambio queda en **Auditoría** con el valor anterior. Los documentos ya emitidos no cambian.' },
    ],
    mensajes: [
      { mensaje: 'El valor debe ser un número no negativo', que_hacer: 'Escribe solo el número, sin letras ni signos.' },
      { mensaje: 'Porcentaje máximo 100', que_hacer: 'Escribe un porcentaje de 0 a 100.' },
      { mensaje: 'El recordatorio de la tasa va de 1 a 168 horas (una semana)', que_hacer: 'Escribe un número de horas de 1 a 168.' },
      { mensaje: 'Los minutos de una autorización van de 1 a 1.440 (un día)', que_hacer: 'Escribe un número de minutos de 1 a 1.440.' },
    ],
    manual: '12.3',
    relacionados: ['impresion'],
    claves: ['parámetros', 'IVA', 'IGTF', 'tope', 'umbral', 'límite de efectivo', 'leyenda', 'plazo', 'vigencia'],
  },
  {
    id: 'configuracion.empresa',
    padre: 'configuracion',
    titulo: 'Datos de la empresa',
    resumen: 'Razón social, RIF, dirección fiscal, teléfonos y correo de la empresa. Salen en la cabecera de notas, cotizaciones, despachos y comprobantes de retención, y en las declaraciones.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Cambiar los datos',
        pasos: [
          'En **Datos de la empresa**, corrige **Razón social**, **RIF**, **Dirección fiscal**, **Teléfonos** o **Correo**.',
          'El RIF va completo, con su letra: por ejemplo J-12345678-9.',
          'Pulsa **Guardar**. El botón se activa cuando cambias algo.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'La factura no imprime estos datos: su papel de imprenta ya los trae.',
          'Los teléfonos del membrete son los de cada tienda: su fijo y su WhatsApp, que se cargan con **Datos y teléfonos** en **Sucursales, almacenes y cajas**. Los **Teléfonos** de la empresa salen solo si ninguna tienda tiene número cargado.',
          'La tarjeta dice si la empresa es contribuyente especial (agente de retención del IVA). Ese dato no se cambia aquí.',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'Cambiar el RIF o la razón social cambia también lo que sale al reimprimir notas, cotizaciones y comprobantes anteriores.' },
    ],
    mensajes: [{ mensaje: 'RIF/CI inválido: elige el prefijo y escribe de 7 a 10 dígitos sin guiones', que_hacer: 'Escribe el RIF completo con su letra y su dígito final, por ejemplo J-12345678-9.' }],
    manual: '12.7',
    claves: ['empresa', 'razón social', 'RIF', 'dirección fiscal', 'membrete', 'contribuyente especial'],
  },
  {
    id: 'configuracion.estructura',
    padre: 'configuracion',
    titulo: 'Sucursales, almacenes y cajas',
    resumen: 'Cada sucursal con sus almacenes y sus cajas. Aquí se crean, se editan y se desactivan. Nada se borra.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra cada sucursal',
        items: [
          'Su nombre y código, su serie (o «sin letra de serie»), la serie del talonario manual, su papel y los renglones que caben en su factura.',
          'Su dirección fiscal, su teléfono fijo y su WhatsApp.',
          'Los botones **Datos y teléfonos**, **Numeración y talonario**, **Papel de factura** y **Desactivar**.',
          'Debajo, sus **Almacenes** y sus **Cajas**: una fila por cada uno, con sus datos a la izquierda y sus botones a la derecha.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Datos y teléfonos de una sucursal',
        pasos: [
          'Pulsa **Datos y teléfonos** en la sucursal.',
          'Corrige el **Código**, el **Nombre** o la **Dirección fiscal** si hace falta.',
          'Escribe el **Teléfono fijo** y el **WhatsApp** (el celular de la tienda), por ejemplo 0251-252.15.06 y 0426-550.70.11. Solo el número: dígitos, espacios, guiones, puntos, paréntesis, + o /. Vacío quiere decir sin número.',
          'Pulsa **Guardar**. Sale «Sucursal actualizada (auditado)»: el fijo y el WhatsApp salen desde ya en el membrete de las notas, cotizaciones y despachos, junto a los de la otra tienda.',
        ],
      },
      { tipo: 'nota', texto: 'La serie y el papel de una sucursal ya creada no se cambian aquí: tienen su propia ventana, **Numeración y talonario** y **Papel de factura**.' },
      {
        tipo: 'pasos',
        titulo: 'Crear o editar una caja',
        pasos: [
          'Pulsa **+ Caja** en la sucursal, o **Editar** en la fila de la caja.',
          'Escribe **Código**, **Nombre**, **Fondo fijo $** y **Fondo fijo Bs**.',
          'Escribe el **Banco del punto de venta**: se anota en cada cobro por punto.',
          'Elige **Qué emite**: **Facturas y notas**, o **Solo facturas (caja fiscal)**.',
          'Elige el **Despacho (entrega de productos)**: media carta vertical o tiquera de 80 mm.',
          'Pulsa **Crear** o **Guardar**. El fondo rige desde la próxima apertura; el banco del punto y lo que emite la caja, desde que guardas.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Crear o editar un almacén',
        pasos: [
          'Pulsa **+ Almacén** en la sucursal, o **Editar** en la fila del almacén.',
          'Escribe **Código** y **Nombre**, y elige el **Tipo**: **Tienda** o **Depósito**.',
          'Escribe la **Dirección (para la guía de traslado)**. Si la dejas vacía, la guía usa la dirección de la tienda.',
          'Al crearlo puedes marcarlo **Principal (reserva stock al vender)**.',
          'Pulsa **Crear** o **Guardar**.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Cambiar el almacén principal',
        pasos: [
          'El principal es de donde sale la venta normal: ahí se aparta y se descuenta lo que se vende.',
          'Pulsa **Hacer principal** en la fila del otro almacén. Un almacén desactivado no trae ese botón: actívalo primero.',
          'La ventana dice cuál es el principal hoy y desde cuál se venderá desde la próxima venta. Pulsa **Sí, hacer principal**.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Crear una sucursal',
        pasos: [
          'Pulsa **+ Sucursal**, arriba a la derecha de la tarjeta.',
          'Escribe **Código** y **Nombre**, la **Serie fiscal** (una letra; vacía es forma libre sin letra) y la **Serie del talonario manual**.',
          'Elige el **Formato de impresión** y escribe la **Dirección fiscal**, el **Teléfono fijo** y el **WhatsApp**.',
          'Pulsa **Crear**. Después agrégale sus almacenes y sus cajas.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Reglas',
        items: [
          'Solo una sucursal puede ir sin letra de serie. La serie no cambia después de emitir documentos.',
          '**Desactivar** / **Activar**: lo desactivado queda en la lista, marcado. Nada se borra. **Desactivar** abre una ventana que dice qué deja de pasar y se confirma con **Desactivar**; **Activar** es directo.',
          'El almacén principal no se desactiva. Una caja con sesión abierta tampoco.',
          'Un almacén con existencia, material por entregar, traslados en camino, un conteo abierto, un ajuste por aprobar, lotes de piezas de vidrio o de productos con detalle a la venta, o reportes de roturas o detalles por aprobar no se desactiva: el mensaje dice qué tiene.',
          'Una sucursal con cajas abiertas o usuarios activos no se desactiva.',
          'Quien recibe de la dueña el permiso de configuración solo cambia la estructura de su propia sucursal: sus datos y teléfonos, sus almacenes, sus cajas, su numeración y su talonario.',
          'El fondo fijo queda en la caja de un turno al siguiente. No es venta: el cierre lo muestra aparte.',
        ],
      },
    ],
    mensajes: [
      { mensaje: 'El almacén principal no se desactiva: designa otro principal primero', que_hacer: 'Haz principal a otro almacén y después desactiva este.' },
      { mensaje: 'La caja tiene una sesión abierta: ciérrala antes de desactivarla', que_hacer: 'Que el cajero la cuente y la cierre en **Cierres de caja**.' },
      { mensaje: '… no se desactiva todavía: tiene … producto(s) con existencia o por entregar, …', que_hacer: 'Traslada o entrega esa mercancía, cierra el conteo o resuelve el ajuste, y vuelve a intentarlo. Los lotes de piezas y de productos con detalle se venden o se les da salida, y los reportes por aprobar se aprueban o se rechazan, en **Roturas y detalles**.' },
      { mensaje: 'Con el permiso delegado solo cambias la estructura de tu sucursal', que_hacer: 'Cambia solo lo de tu sucursal. Lo de la otra lo hace la dueña.' },
      { mensaje: '… está desactivado: actívalo antes de hacerlo principal', que_hacer: 'Activa el almacén y después hazlo principal.' },
      { mensaje: '… no se desactiva todavía: tiene … caja(s) abierta(s) y … usuario(s) activo(s)', que_hacer: 'Cierra sus cajas y desactiva a sus usuarios, o pásalos a otra sucursal.' },
      { mensaje: 'Ya hay una sucursal que factura sin letra de serie', que_hacer: 'Ponle una letra a la **Serie fiscal** de esta sucursal.' },
      { mensaje: 'Ya existe la caja … en esa sucursal', que_hacer: 'Usa otro código para la caja.' },
      { mensaje: 'Escribe solo el número (dígitos, espacios, guiones, puntos o paréntesis)', que_hacer: 'El teléfono fijo o el WhatsApp trae letras u otros signos. Escribe solo el número, o déjalo vacío si la tienda no tiene.' },
    ],
    manual: '12.4',
    relacionados: ['caja', 'impresion'],
    claves: ['sucursal', 'almacén', 'depósito', 'tienda', 'caja', 'fondo fijo', 'principal', 'serie', 'punto de venta', 'tiquera', 'teléfono', 'WhatsApp', 'membrete', 'datos y teléfonos'],
  },
  {
    id: 'configuracion.numeracion',
    padre: 'configuracion',
    titulo: 'Numeración y talonario',
    resumen: 'Por sucursal: el último número usado de cada documento (para seguir los del sistema anterior), el talonario de papel fiscal en uso y la serie del talonario manual.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Numeración de arranque',
        pasos: [
          'En **Sucursales, almacenes y cajas**, pulsa **Numeración y talonario** en la sucursal.',
          'En cada tipo (**Factura**, **Nota de entrega**, **Nota de crédito**, **Nota de débito** y **Número de control (papel fiscal)**) escribe el ÚLTIMO número ya usado.',
          'Pulsa el **Guardar** de esa fila. Al lado sale el **próximo** que saldrá.',
          'La factura, la nota de crédito y la nota de débito salen con el número real, sin ceros delante (Barquisimeto «28925», Barinas «C-33»); los demás documentos, con 6 dígitos.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Cargar un talonario de papel fiscal',
        pasos: [
          'Al recibir el talonario, escribe el rango que trae impreso al pie de las hojas: **Desde** y **Hasta**.',
          'La **Nota (imprenta, fecha)** es opcional.',
          'Si el talonario nuevo no sigue al último número de control usado, la ventana avisa antes de cargarlo: «Ojo: el último número de control usado es … y este talonario empieza en …: se saltan … número(s)». Si las hojas vienen así de la imprenta, cárgalo: queda anotado en la auditoría y sale el aviso «Se saltaron … número(s) de control».',
          'Pulsa **Cargar talonario**. El recuadro muestra el talonario en uso, cuántas hojas quedan y cuántas hojas en blanco se anularon.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Reglas del talonario',
        items: [
          'Factura, nota de crédito y nota de débito comparten un solo consecutivo de control por sucursal.',
          'Un talonario nuevo no repite números de otro ya cargado.',
          'Si escribiste mal el que está en uso, cárgalo otra vez con el rango correcto: sale **Talonario en uso corregido**. El rango debe incluir el último control usado y los ya emitidos con él.',
          'Cargar o corregir un talonario nunca hace retroceder el número de control.',
          'Agotado el talonario no se factura hasta cargar el nuevo. La nota de entrega sigue saliendo: no usa papel fiscal.',
          'La dueña y el administrador reciben avisos cuando quedan pocas hojas. El primero sale según **Avisar cuando al talonario fiscal le queden (hojas)**.',
          'Sin talonario cargado, el control se numera de corrido y no hay aviso de papel.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Talonario manual',
        pasos: [
          'Es la serie de las facturas hechas a mano cuando no hay sistema (A en Barquisimeto, B en Barinas).',
          'Escribe la **Serie (una letra)** y pulsa su **Guardar**.',
          'Esas facturas se registran después en **Libros**, en **Facturas manuales**.',
          'Como la serie fiscal, no se cambia después de registrar la primera factura manual con ella.',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'Un número no se puede bajar de lo que este sistema ya emitió. Revisa el último número antes de guardar: la numeración es fiscal.' },
    ],
    mensajes: [
      { mensaje: 'El sistema ya emitió hasta el … en …: el último número no puede ser menor', que_hacer: 'Escribe un número igual o mayor.' },
      { mensaje: 'Ese rango comparte números con un talonario anterior ya cargado (…)', que_hacer: 'Revisa el número inicial y el final: un talonario nuevo no repite números.' },
      { mensaje: 'Ese rango comparte números con el talonario en uso (…)', que_hacer: 'Para corregirlo, incluye el último control usado y los ya emitidos. Si es otro talonario, que no repita números.' },
      { mensaje: 'A ese talonario no le quedan hojas: el último número de control usado es …', que_hacer: 'Revisa el rango. Si ese último número está mal, corrígelo primero en **Número de control (papel fiscal)**, arriba en la misma ventana.' },
      { mensaje: 'El próximo control (…) quedaría fuera del talonario cargado (…)', que_hacer: 'El número de control debe quedar dentro del talonario en uso: revisa el número o carga el talonario correcto.' },
      { mensaje: 'La serie del talonario manual no se cambia después de registrar facturas manuales con ella', que_hacer: 'Ya hay facturas manuales con esa serie: déjala como está.' },
      { mensaje: 'Con el permiso delegado solo cambias la estructura de tu sucursal', que_hacer: 'Con el permiso que te dio la dueña solo cambias la numeración y el talonario de tu sucursal. Los de la otra los cambia la dueña.' },
    ],
    manual: '12.5',
    relacionados: ['impresion', 'libros'],
    claves: ['numeración', 'correlativo', 'talonario', 'número de control', 'papel fiscal', 'serie', 'factura manual', 'hojas'],
  },
  {
    id: 'configuracion.papel',
    padre: 'configuracion',
    titulo: 'Papel de factura',
    resumen: 'Dónde imprime el sistema el cuerpo de la factura sobre el papel de la imprenta de cada sucursal. Se calibra una vez, con la impresora y el papel reales.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Calibrar',
        pasos: [
          'En **Sucursales, almacenes y cajas**, pulsa **Papel de factura** en la sucursal.',
          'Elige el **Tamaño de la hoja**: carta, media carta horizontal o media carta en hoja carta de pie (Barinas: la factura sale en la mitad de arriba y la hoja se corta). Al cambiarlo vuelven las medidas de partida de ese papel.',
          'Elige la **Letra**: ancho fijo (matriz de punto Epson LX-300) o proporcional (láser HP).',
          'Revisa las medidas en milímetros: **Margen superior (mm)** hasta «Cliente», **Margen inferior (mm)** hasta «Total Bs.», **Margen izquierdo (mm)** y **Margen derecho (mm)**.',
          'Revisa los **Renglones que caben**: es el tope de renglones de una factura.',
          'Pulsa **Guardar y probar**. Se abre la hoja de prueba: una factura de muestra llena, con reglas en milímetros.',
          'Imprímela sobre una hoja preimpresa ya anulada o una fotocopia, con márgenes «Ninguno» y escala 100 %.',
          'Si queda alta o baja, mide con la regla cuántos milímetros mover, cámbialos y repite hasta que calce.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'Medidas de partida: carta, superior 45 mm, inferior 32 mm, izquierdo 4 mm y derecho 20 mm, 36 renglones; media carta en hoja carta de pie, las mismas de la horizontal (el inferior, desde la mitad de la hoja); media carta horizontal, superior 39 mm, inferior 14 mm, izquierdo 6 mm y derecho 8 mm, 9 renglones.',
          'Al imprimir, en Chrome: papel carta (Letter), márgenes «Ninguno» (None) y escala predeterminada (Default, el 100 %). Los márgenes de la factura los pone esta ventana, no Chrome.',
          'Límites: de 0 a 150 mm arriba, de 0 a 100 mm abajo, de 0 a 40 mm a los lados, y de 1 a 60 renglones.',
          'El tamaño de la hoja es el papel de la sucursal: con él salen también la nota de entrega y el Cambio.',
          'La caja no emite una factura con más renglones de los que caben: pide dividir la venta.',
          '**Guardar** guarda sin abrir la hoja de prueba.',
          'Con la letra de ancho fijo (matriz de punto), la factura sale en una letra más grande, más gruesa y más separada para que se lea bien, sin mover los márgenes. Un nombre de producto muy largo se corta en una letra completa; un monto, el nombre del cliente o su teléfono que no caben salen un poco más pequeños, pero completos.',
          'En la impresora, usa la calidad **NLQ** y no «borrador»: la letra sale más nítida.',
          'La negrita y la inclinación de los rótulos las dibuja el sistema: la factura sale en el papel igual que en la pantalla, con cualquier versión de Chrome.',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'La factura en forma libre solo vale sobre el papel de la imprenta. Para probar, no gastes una hoja buena: usa una ya anulada o una fotocopia.' },
    ],
    manual: '28.2',
    relacionados: ['impresion'],
    claves: ['papel', 'márgenes', 'calibrar', 'hoja de prueba', 'renglones', 'impresora', 'Epson', 'preimpreso'],
  },
  {
    id: 'configuracion.catalogo',
    padre: 'configuracion',
    titulo: 'Listas de precio y categorías',
    resumen: 'Los catálogos que hacen falta antes de cargar productos y clientes. Nada se borra: lo que deja de usarse se desactiva.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Agregar una lista de precio',
        pasos: [
          'Escribe el nombre de la lista (por ejemplo «Lista 1») y pulsa **Agregar**.',
          'La lista nueva aparece en la ficha de cada producto, para ponerle su precio, y en la de cada cliente.',
          '**Desactivar** / **Activar** la quitan o la devuelven. **Desactivar** pide confirmar en una ventana; **Activar** es directo. La marcada **la de un cliente nuevo** no se desactiva.',
          'Una lista que tienen clientes activos tampoco se desactiva: primero cámbiales la lista en su ficha, con **Lista y crédito**.',
          'Las listas de precio valen para toda la empresa: las crea y las desactiva solo la dueña. Quien tiene el permiso de configuración delegado las ve sin esos botones.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Agregar una categoría',
        pasos: ['Escribe el nombre de la categoría.', 'Elige **Categoría principal**, o **Dentro de …** para crear una subcategoría.', 'Pulsa **Agregar**.'],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'Cada producto lleva un precio por lista y cada cliente compra con la suya.',
          'Las categorías ordenan el catálogo y sirven para los descuentos programados.',
          '**Desactivar** una categoría pide confirmar en una ventana; **Activar** es directo. Al desactivar una categoría principal se desactivan también sus subcategorías. Los productos que ya la tienen la conservan, pero no se ofrece para productos nuevos.',
          'La tarjeta no tiene botón para cambiar el nombre de una lista o de una categoría: escríbelo bien antes de pulsar **Agregar**.',
        ],
      },
    ],
    mensajes: [
      { mensaje: 'La categoría «…» no existe: pídele a la dueña que la cree en Configuración', que_hacer: 'Créala aquí, en **Categorías de producto**.' },
      { mensaje: '… cliente(s) activo(s) tienen la lista …: cámbiales la lista en su ficha antes de desactivarla', que_hacer: 'Cambia la lista de esos clientes en su ficha, con **Lista y crédito**, y vuelve a desactivarla.' },
    ],
    manual: '12.9',
    relacionados: ['productos', 'clientes'],
    claves: ['lista de precio', 'lista', 'detal', 'mayorista', 'categoría', 'subcategoría'],
  },
  {
    id: 'configuracion.firma',
    padre: 'configuracion',
    titulo: 'Firma de los comprobantes de retención',
    resumen: 'La imagen de la firma que sale en «Firma y sello agente de retención» de los comprobantes que se entregan a los proveedores.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Cargar la firma',
        pasos: ['Pulsa **Cargar firma** y elige la imagen: PNG o JPG, hasta 300 KB.', 'La firma se ve en el recuadro de la tarjeta.', '**Cambiar firma** la reemplaza y **Quitar** la elimina.'],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: ['Sin firma cargada, el comprobante deja el espacio para firmar a mano.', 'Solo la ve quien entra a **Compras**: no queda como archivo público.', 'Cada cambio queda en **Auditoría**.'],
      },
    ],
    mensajes: [{ mensaje: 'La imagen pesa … KB; el máximo es 300 KB', que_hacer: 'Usa una imagen más liviana: recórtala o guárdala más pequeña.' }],
    manual: '12.8',
    relacionados: ['compras', 'retenciones'],
    claves: ['firma', 'sello', 'comprobante de retención', 'agente de retención'],
  },

  // ─── Auditoría ──────────────────────────────────────────────────────────
  {
    id: 'auditoria',
    titulo: 'Auditoría',
    rutas: ['/auditoria'],
    permiso: { clave: 'auditoria.ver' },
    resumen: 'El registro de todo lo que pasa en el sistema: quién, cuándo, desde qué sucursal y el antes y el después de cada cambio. Nada se puede borrar de aquí.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          'Cada fila: **Fecha** (con segundos), **Usuario**, **Suc.** (o «global»), **Evento**, **Entidad** y el **Cambio**, con lo de antes tachado en rojo y lo nuevo en verde.',
          'Los datos más comunes salen con su nombre («Correo», «Rol», «Sucursal», «Monto $», «Observación»…), no con el interno. Un alta, una impresión o un cierre se lee dato por dato; lo que no tiene nombre propio sale tal como se guardó.',
          'Los bloqueos por claves fallidas y las reaperturas de caja se marcan en rojo.',
          'Algunos datos de tasas salen como «(solo la dueña)»: se ve que cambiaron, pero el número solo lo ve ella, también en el Excel.',
          'Salen 25 eventos por página: **Anterior** y **Siguiente** para moverte.',
        ],
      },
      { tipo: 'nota', texto: 'Tú ves todos los números. Los demás ven «(solo la dueña)» en lugar de la tasa 2 y el factor de un cambio de tasa, el factor de una cotización re-tasada y la tasa 2 de un cierre declarado, también dentro del cierre que guarda una reapertura de caja, en la pantalla y en el Excel.', permiso: VE_TASA_2 },
      {
        tipo: 'pasos',
        titulo: 'Buscar algo',
        pasos: [
          '**Evento**: escribe cómo empieza el nombre del evento, como sale en la columna **Evento** (por ejemplo «login» o «caja.»).',
          '**Entidad**: escribe el nombre exacto que sale en la columna **Entidad**.',
          'Elige el **Usuario** y la **Sucursal** en sus listas, si te salen.',
          'Acota las fechas desde y hasta. Van en hora de Caracas.',
          'Para llevarlo a Excel, pulsa **Exportar XLSX**: baja lo que coincide con los filtros, hasta los 5.000 más recientes. Si hay más, lo dice en una fila al final.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Quién ve qué',
        items: [
          'La dueña y el contador ven todas las sucursales y filtran por **Sucursal**.',
          'El filtro **Usuario** sale solo a quien administra usuarios: la dueña, o a quien ella le dio el permiso «Usuarios, sucursales y configuración».',
          'El contador filtra por sucursal, evento, entidad y fechas.',
          'El administrador ve solo lo de su sucursal, sin el filtro **Sucursal**.',
          'Si la dueña está operando una sucursal, ve solo lo de esa sucursal.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Qué queda registrado, entre otras cosas',
        items: [
          'Las entradas al sistema, las claves fallidas y los bloqueos.',
          'Cada vez que la dueña toma una sucursal, con su motivo.',
          'Cada impresión y reimpresión de un documento, también de cotizaciones, despachos, guías, comprobantes de retención, retiros, cierres y recibos de abono.',
          'Cada autorización pedida, aprobada, negada o usada.',
          'Los cambios de permisos, de precios, de tasas y de parámetros, la numeración y el talonario.',
          'Los retiros de caja, los reconteos, las reaperturas y las cargas por Excel.',
          '**Guardar físico** de un conteo, el aviso de retenciones con más de 30 días y la edición de productos y equipos, con el antes y el después completos.',
          'Cada archivo adjuntado o borrado (con su nombre, su tipo, su tamaño y a qué pertenece, nunca el archivo) y cada correo enviado (el documento, a quién, el asunto y los archivos que llevó).',
        ],
      },
    ],
    manual: '26',
    relacionados: ['general', 'configuracion'],
    claves: ['auditoría', 'historial', 'quién hizo', 'bitácora', 'registro', 'exportar', 'antes y después', 'excel'],
  },
];
